import { CONFIGURATION_STATUS_TYPE, TEXT_LENGTH } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Option } from 'src/shared/form/control'
import { Form, FormSpy } from 'react-final-form'
import { Box, Grid2, Stack, Typography } from '@mui/material'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { maxChar, required } from 'src/shared/form/validation/validators'
import { TextField } from 'src/shared/form/control/TextField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { Dispatch, useCallback, useState } from 'react'
import { usePrompt } from 'src/routing/routing-hooks'
import Alert from '@mui/material/Alert'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsGridUtils'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { AddButton } from 'src/shared/button/Buttons'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'

export interface SubprinciplesConfigurationFormValues {
  parentPrinciple: number | null
  enName: string
  deName: string
  frName: string
  itName: string
  status: CONFIGURATION_STATUS_TYPE
  enHelpInstructions: string
  deHelpInstructions: string
  frHelpInstructions: string
  itHelpInstructions: string
  helpAndInstructionsKey: string
}

interface SubprinciplesFormFieldOptions {
  statusFieldOptions: Option[]
  parentPrincipleOptions: Option[]
}

interface SubprinciplesConfigurationFormProps {
  mode: 'CREATE' | 'EDIT'
  onSubmit: (values: SubprinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => Promise<void>
  initialValues: SubprinciplesConfigurationFormValues
  formFieldOptions: SubprinciplesFormFieldOptions
  isSubprincipleAssessedInPrograms?: boolean
}

export const SubprinciplesConfigurationForm = ({
  mode,
  onSubmit,
  initialValues,
  formFieldOptions,
  isSubprincipleAssessedInPrograms,
}: SubprinciplesConfigurationFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()
  const navigate = useDelayedNavigate()

  const [formDirty, setFormDirty] = useState<boolean>(false)

  const handleAddHelpInstructionsLink = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, helpAndInstructionsKey: string) => {
      e.stopPropagation()

      const searchParamsString = `${HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM}=${helpAndInstructionsKey}`
      navigate(`${ROUTES.HelpInstructionsConfigurationCreate.params({})}?${searchParamsString}`)
    },
    [navigate],
  )

  usePrompt(getMessage('notification.form.unsaved.changes'), formDirty)

  return (
    <Stack spacing={3}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      {isSubprincipleAssessedInPrograms && (
        <Alert severity="info">
          {getMessage('label.configuration.subprinciples.edit.info.parent.principle.change')}
        </Alert>
      )}
      <Form<SubprinciplesConfigurationFormValues>
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values, setFormDirty)}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="subprinciples-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={5}>
                <Grid2 container rowSpacing={1} columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={formFieldOptions.statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable={true}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={formFieldOptions.parentPrincipleOptions}
                      name="parentPrinciple"
                      label={getMessage('label.configuration.subprinciples.grid.parent.principle')}
                      disableClearable={true}
                      required
                      validate={required()}
                      disabled={isSubprincipleAssessedInPrograms}
                    />
                  </Grid2>

                  <Grid2 size={6}>
                    <TextField name="deName" label={`${getMessage('label.name')} DE`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enName" label={`${getMessage('label.name')} EN`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frName" label={`${getMessage('label.name')} FR`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itName" label={`${getMessage('label.name')} IT`} required validate={required()} />
                  </Grid2>
                </Grid2>

                <Stack spacing={3}>
                  <Typography variant="h2">{getMessage('label.help.and.instructions')}</Typography>
                  <HtmlEditorField
                    name="deHelpInstructions"
                    label={getMessage('label.language.de')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={maxChar(TEXT_LENGTH.XL)}
                  />
                  <HtmlEditorField
                    name="enHelpInstructions"
                    label={getMessage('label.language.en')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={maxChar(TEXT_LENGTH.XL)}
                  />
                  <HtmlEditorField
                    name="frHelpInstructions"
                    label={getMessage('label.language.fr')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={maxChar(TEXT_LENGTH.XL)}
                  />
                  <HtmlEditorField
                    name="itHelpInstructions"
                    label={getMessage('label.language.it')}
                    formLabelProps={{ sx: { fontWeight: 'bold' } }}
                    validate={maxChar(TEXT_LENGTH.XL)}
                  />
                </Stack>
              </Stack>

              <FormSpy
                subscription={{ dirty: true }}
                onChange={(props: { dirty: boolean }) => {
                  setFormDirty(props.dirty)
                }}
              />
            </form>
          )
        }}
      />
      {mode === 'EDIT' && (
        <Box display="flex" justifyContent="start">
          <AddButton
            messageKey={'label.configuration.subprinciples.add.help.instructions.link.button'}
            startIcon={<AddOutlinedIcon />}
            onClick={(e) => handleAddHelpInstructionsLink(e, initialValues.helpAndInstructionsKey)}
          />
        </Box>
      )}
    </Stack>
  )
}
